<template>
  <div class="h-100">
    <b-workspace>
      <b-tree-view
        :config="{
          card: {
            gap: 30,
            height: 200,
          },
        }"
        :items="formattedDepartmentList"
        @add:click="createDepartment($event.item.id)">
        <template #card="{ item, props, toggleExpand, setActive, isExpanded }">
          <v-hover>
            <template #default="{ isHovering, props: hoverProps }">
              <v-card
                :elevation="isHovering ? 8 : 1"
                :ripple="false"
                class="bg-white"
                rounded="xl"
                v-bind="{ ...props, ...hoverProps }"
                variant="elevated"
                @click="
                  () => {
                    setActive()
                    if (!isExpanded) {
                      toggleExpand()
                    }
                  }
                ">
                <div class="d-flex flex-column justify-space-between h-100">
                  <v-card-text>
                    <div class="d-flex justify-space-between align-center mb-2">
                      <h4 class="text-grey-darken-2">
                        {{ item.name }}
                      </h4>

                      <b-action-menu
                        :actions="[
                          {
                            title: 'Редагувати',
                            icon: 'mdi-pencil',
                            action: () => updateDepartment(item.id),
                          },
                          {
                            title: 'Видалити',
                            icon: 'mdi-delete',
                            action: () => deleteDepartment(item.id),
                            listItemProps: {
                              class: 'text-error',
                            },
                          },
                        ]"></b-action-menu>
                    </div>

                    <div class="mb-5">
                      <span class="text-disabled">Керівник:</span>
                      {{ getPersonName(item.supervisor?.user) || 'Не вказано' }}
                    </div>

                    <v-menu :close-on-content-click="false">
                      <template #activator="{ props: menuProps }">
                        <v-chip
                          class="text-grey-darken-2"
                          density="compact"
                          v-bind="menuProps"
                          variant="outlined"
                          @click.stop>
                          Співробітників {{ item.employees.length }}
                        </v-chip>
                      </template>

                      <v-card class="pa-2">
                        <v-data-table
                          :headers="departmentUserListHeaders.slice(0, 2)"
                          :hide-default-footer="item.employees.length <= 10"
                          :items="item.employees"
                          :items-per-page="10"
                          density="compact"
                          item-value="id">
                          <template #item.name="{ item: i }">
                            <button
                              class="link"
                              type="button"
                              @click="openEmployeeCard(i.user)">
                              {{ getPersonName(i.user) }}
                            </button>
                          </template>
                          <template #item.isActive="{ item: employeeItem }">
                            <v-chip
                              :color="
                                employeeItem.user.isActive ? 'success' : 'error'
                              "
                              size="small">
                              {{
                                employeeItem.user.isActive
                                  ? 'Активний'
                                  : 'Звільнений'
                              }}
                            </v-chip>
                          </template>
                        </v-data-table>
                      </v-card>
                    </v-menu>
                  </v-card-text>
                  <div>
                    <v-btn
                      v-if="item.children?.length"
                      block
                      class="font-size-12 text-lowercase"
                      color="grey-darken-1"
                      variant="tonal"
                      @click.stop="
                        () => {
                          setActive()
                          toggleExpand()
                        }
                      ">
                      <span class="mr-2">
                        відділів: {{ item.children.length }}
                      </span>
                      <v-icon size="x-large">
                        mdi-chevron-{{ isExpanded ? 'up' : 'down' }}
                      </v-icon>
                    </v-btn>
                    <v-btn
                      v-else
                      block
                      class="font-size-12 text-lowercase"
                      color="black"
                      disabled
                      variant="text"
                      @click="toggleExpand">
                      Немає відділів у підпорядкуванні
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </template>
          </v-hover>
        </template>
      </b-tree-view>
    </b-workspace>
  </div>
</template>

<script lang="ts">
import { BActionMenu, BTreeView, BWorkspace } from 'best-modules/components'
import {
  departmentListHeaders,
  departmentUserListHeaders,
} from '@/utils/tableHeaders'
import { computed, defineComponent } from 'vue'
import { getPersonName, mapKeysDeep } from 'best-modules/utils'
import type { DepartmentListItem as D } from '@/utils/types/departments'
import { useDepartmentList } from './index'
import { openUserCardDialog } from '@/utils/userCardDialog'

type DepartmentListItem = Omit<D, 'allChildren'> & {
  children: DepartmentListItem[]
}

export default defineComponent({
  name: 'TreeView',
  components: { BWorkspace, BTreeView, BActionMenu },
  data() {
    return { departmentListHeaders, departmentUserListHeaders }
  },
  methods: { getPersonName },
  setup() {
    const {
      departmentList,
      createDepartment,
      updateDepartment,
      deleteDepartment,
    } = useDepartmentList()
    const formattedDepartmentList = computed<DepartmentListItem[]>(() => {
      return mapKeysDeep(departmentList.value, (_, key) => {
        if (key === 'allChildren') {
          return 'children'
        } else {
          return key
        }
      })
    })

    const openEmployeeCard = (user: any) => {
      openUserCardDialog({
        userId: user.id,
        mode: 'employee',
        context: 'list',
        title: getPersonName(user),
      })
    }

    return {
      departmentList,
      formattedDepartmentList,
      createDepartment,
      updateDepartment,
      deleteDepartment,
      openEmployeeCard,
    }
  },
})
</script>

<style lang="scss" scoped>
.link {
  background: transparent;
  border: 0;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
</style>
